// components/task/TaskSidebar.tsx
import React, { useState } from "react";
import { Button } from "react-bootstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faClone,
  faFlag as faFlagRegular,
  faStickyNote,
  faUser,
} from "@fortawesome/free-regular-svg-icons"; import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import avtar from "../../../assets/images/avtar.png"; // Adjust path as needed based on your folder structure
import NoDataFound from "./NoDataFound"; // Adjust path
import { getTimeAgo } from "../../../utilities/dateUtils"; // Adjust path
import Task from "../../../interfaces/task/Task";
import TaskAction from "../../../interfaces/task/taskActions/TaskAction";
import { TaskProgressNoteDto } from "../../../services/weeklySupervision/weeklySupervisionService";
import { faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { PiFlagFill } from "react-icons/pi";
import DefaultAvatar from "../../ui/Avatar/DefaultAvatar";

const Flag = PiFlagFill as React.ComponentType<any>;

const STATUS_BADGE_COLORS: Record<string, string> = {
  "NOT STARTED": "#e8e8e8",
  "IN REVIEW (SUP)": "#FFC446",
  "IN REVIEW (PM)": "#FFC446",
  "COMPLETED": "#037010",
  "Pending Extension": "#BE68E0",
  "Extended": "#FF46E6",
  "READY_": "#5DC837",
  "ISSUES FOUND": "#FF5555",
  "CLOSED": "#898989",
  "Deleted": "#ACACAC",
  "IN PROGRESS": "#5DC837"
};

interface TaskSidebarProps {
  task: Task;
  activities?: TaskAction[] | null;
  currentUser: any;
  progressNotes: string;
  setProgressNotes: (value: string) => void;
  handleSaveNotes: () => void;
  isNoteLoading: boolean;
  progressNoteData: TaskProgressNoteDto | null;
  closureRequest?: any;
  handleShowExtendTaskRequestModal: () => void;
}

const TaskSidebar: React.FC<TaskSidebarProps> = ({
  task,
  activities,
  currentUser,
  progressNotes,
  setProgressNotes,
  handleSaveNotes,
  isNoteLoading,
  progressNoteData,
  closureRequest,
  handleShowExtendTaskRequestModal,
}) => {
  const navigate = useNavigate();
  const [selectedSidebarTab, setSelectedSidebarTab] = useState<"details" | "instructions" | "extensions" | "latejustify">("details");
  const [isSupervisionNotesExpanded, setIsSupervisionNotesExpanded] = useState(false);

  // --- NEW DUE DATE LOGIC ---
  const getDueDateStatus = () => {
    if (!task || ["COMPLETED", "DELETED"].includes(task.status.name)) {
      return { className: "", label: "", isLate: false, isAtRisk: false, svg: "" };
    }

    const today = moment().startOf("day");
    const dueDate = moment(task.endDate).startOf("day");
    const daysUntilDue = dueDate.diff(today, "days");

    // LATE: Due date is in the past (negative diff)
    if (daysUntilDue < 0) {
      return {
        className: "text-[#EF4444]", // Tailwind red-500 equivalent
        label: " (Late)",
        isLate: true,
        isAtRisk: false,
        svg: "w-6 h-6 flex items-center text-white p-1 justify-center rounded-full bg-[#EF4444]", // Red Icon
      };
    }

    // DUE SOON: Within 0 to 2 days
    if (daysUntilDue >= 0 && daysUntilDue <= 2) {
      return {
        className: "text-[#D57C00]",
        label: " (Due Soon)",
        isLate: false,
        isAtRisk: true,
        svg: "w-6 h-6 flex items-center text-black p-1 justify-center rounded-full bg-[#FFF4CA]", // Yellow Icon
      };
    }

    // NORMAL
    return {
      className: "text-[#1f2937]", // Default dark gray
      label: "",
      isLate: false,
      isAtRisk: false,
      svg: "w-6 h-6 flex items-center text-black p-1 justify-center rounded-full bg-[#E5E7EB]", // Gray Icon
    };
  };

  const dueDateStatus = getDueDateStatus();

  // Determine if extension button should be shown
  // Condition: Task allows extension AND (Is Late OR Is Due Soon) AND not already pending
  const showExtensionButton =
    task?.canExtent &&
    !task.isPendingExtension &&
    (dueDateStatus.isAtRisk || dueDateStatus.isLate);

  const handleAssigneeClick = () => {
    const participantAssignee = task?.assignees?.find((user) => !user.isReporter && !user.isCoAssignee);
    if (!participantAssignee?.user?.id) return;
    navigate(`/profile/user/${participantAssignee.user.id}/files`);
  };

  const handleParticipantClick = () => {
    if (!task?.participant?.id) return;
    navigate(`/participant/${task.participant.id}/participant-snapshot`);
  };

  const participantAssignee = task?.assignees?.find((user) => !user.isReporter && !user.isCoAssignee);

  const getStatusBadgeClassName = (statusName: string) => {
    const baseClass = "badge-status-custom";
    const normalizedStatus = statusName.toUpperCase().trim();
    switch (normalizedStatus) {
      case "NOT STARTED": return `${baseClass} badge-status-custom-not-started`;
      case "IN REVIEW (SUP)": return `${baseClass} badge-status-custom-in-review-sup`;
      case "IN REVIEW (PM)":
      case "IN REVIEW": return `${baseClass} badge-status-custom-in-review`;
      case "COMPLETED": return `${baseClass} badge-status-custom-completed`;
      case "PENDING EXTENSION": return `${baseClass} badge-status-custom-pending-extension`;
      case "EXTENDED": return `${baseClass} badge-status-custom-extended`;
      case "PENDING": return `${baseClass} badge-status-custom-pending`;
      case "IN PROGRESS": return `${baseClass} badge-status-custom-in-progress`;
      case "READY":
      case "READY_": return `${baseClass} badge-status-custom-ready`;
      case "ISSUES FOUND": return `${baseClass} badge-status-custom-issues`;
      case "BLOCKED": return `${baseClass} badge-status-custom-blocked`;
      case "DELETED": return `${baseClass} badge-status-custom-closed`;
      default: return `${baseClass} badge-status-custom-default`;
    }
  };

  const renderExtensionHistory = () => (
    <div className="extension-history-container h-full">
      {!task?.extensionHistory || task.extensionHistory.length === 0 ? (
        <NoDataFound
          title="No Extensions Found"
          description="There are no extensions to show you right now"
        />
      ) : (
        <div className="extension-history-list">
          {task.extensionHistory.map((item, index) => {
            const isPending = item.status === "PENDING";
            const isApproved = item.status === "APPROVED";
            const isRejected = item.status === "REJECTED";

            return (
              <div key={item.requestId} className="extension-request-card">
                <div className="extension-status-badge">
                  {isPending && "Pending"}
                  {isApproved && `Approved : ${item.decisionTime ? moment(item.decisionTime).format("DD MMMM, YYYY") : "--"}`}
                  {isRejected && `Rejected : ${item.decisionTime ? moment(item.decisionTime).format("DD MMMM, YYYY") : "--"}`}
                </div>
                <div className="extension-details-grid">
                  <div className="extension-detail-row">
                    <span className="extension-detail-label">Requested By :</span>
                    <span className="extension-detail-value">{item.requesterName ?? "--"}</span>
                  </div>
                  {/* ... (Existing logic for approver rows) ... */}
                  {isPending && (
                    <div className="extension-detail-row">
                      <span className="extension-detail-label">
                        Pending with :
                      </span>
                      <span className="extension-detail-value">
                        {item.approverName ?? "--"}
                      </span>
                    </div>
                  )}
                  {isApproved && (
                    <div className="extension-detail-row">
                      <span className="extension-detail-label">
                        Approved By :
                      </span>
                      <span className="extension-detail-value">
                        {item.approverName ?? "--"}
                      </span>
                    </div>
                  )}
                  {isRejected && (
                    <div className="extension-detail-row">
                      <span className="extension-detail-label">
                        Rejected By :
                      </span>
                      <span className="extension-detail-value">
                        {item.approverName ?? "--"}
                      </span>
                    </div>
                  )}
                  <div className="extension-detail-row">
                    <span className="extension-detail-label">Old Due Date :</span>
                    <span className="extension-detail-value">{item.oldEndDate ? moment(item.oldEndDate).format("DD MMMM, YYYY") : "--"}</span>
                  </div>
                  <div className="extension-detail-row">
                    <span className="extension-detail-label">New Due Date :</span>
                    <span className="extension-detail-value">{item.newEndDate ? moment(item.newEndDate).format("DD MMMM, YYYY") : "--"}</span>
                  </div>
                  <div className="extension-detail-row extension-reason-row">
                    <span className="extension-detail-label">Reason :</span>
                    <span className="extension-detail-value extension-reason-text">{item.comment ?? "--"}</span>
                  </div>
                  <div className="extension-detail-row">
                    <span className="extension-detail-label">Requested Date :</span>
                    <span className="extension-detail-value">{item.requestTime ? moment(item.requestTime).format("DD MMMM, YYYY") : "--"}</span>
                  </div>
                </div>
                {index < task.extensionHistory!.length - 1 && <div className="extension-separator"></div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="task-sidebar">
      <div className="task-details-card">
        <div className="details-tabs">
          <span className={`tab ${selectedSidebarTab === "details" ? "active" : ""}`} onClick={() => setSelectedSidebarTab("details")}>
            Task Details
          </span>
          {/* <span className={`tab ${selectedSidebarTab === "instructions" ? "active" : ""}`} onClick={() => setSelectedSidebarTab("instructions")}>
            Task Instructions
          </span> */}
          <span className={`tab ${selectedSidebarTab === "extensions" ? "active" : ""}`} onClick={() => setSelectedSidebarTab("extensions")}>
            Extensions <span className="tab-count-pill">{task.extensionCount ?? 0}</span>
          </span>
          <span className={`tab ${selectedSidebarTab === "latejustify" ? "active" : ""}`} onClick={() => setSelectedSidebarTab("latejustify")}>
            Late Justify
          </span>
        </div>

        <div className="details-content">
          {selectedSidebarTab === "details" ? (
            <>
              {/* Status Section */}
              <div className="detail-content-section">
                {/* First line: Flag SVG (left) + Status text (right) */}
                <div
                  style={{
                    display: "flex",
                    width: "25.69063rem",
                    height: "1.42725rem",
                    alignItems: "center",
                    gap: "0.57088rem"
                  }}
                  className="mb-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 19 19" fill="none">
                    <path d="M3.04492 16.7465V3.04481C3.04492 2.92664 3.07244 2.81009 3.12528 2.70439C3.17813 2.59869 3.25487 2.50675 3.3494 2.43585C4.13997 1.84292 5.10153 1.5224 6.08974 1.5224C8.37335 1.5224 9.89576 3.04481 11.6717 3.04481C12.6866 3.04481 13.4648 2.84182 14.0063 2.43585C14.1194 2.35103 14.2538 2.29938 14.3946 2.28668C14.5354 2.27398 14.677 2.30075 14.8034 2.36397C14.9299 2.42719 15.0362 2.52437 15.1105 2.64462C15.1848 2.76487 15.2242 2.90344 15.2242 3.04481V10.6569C15.2242 10.775 15.1967 10.8916 15.1438 10.9973C15.091 11.103 15.0143 11.1949 14.9197 11.2658C14.1291 11.8587 13.1676 12.1793 12.1794 12.1793C9.89576 12.1793 8.37335 10.6569 6.08974 10.6569C4.96629 10.6569 3.88227 11.071 3.04492 11.82" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="detail-label-text">Status</span>
                </div>

                {/* Second line: Status badge aligned with Status text position - using badgeBaseClass design from TasksPageTable */}
                <div style={{ marginLeft: "calc(16px + 0.57088rem)" }}>
                  <span
                    style={{ justifyContent: "center" }}
                    className="uppercase text-[11px] capitalize text-black pt-1.5 pb-1 w-fit !px-3 flex items-center justify-center rounded-[6px] whitespace-nowrap gap-2 bg-[#F4F4F4]"
                  >
                    {/* Flag */}
                    <span className="flex items-center justify-center w-4 h-6">
                      <Flag
                        className="w-4 h-4"
                        style={{
                          fill: STATUS_BADGE_COLORS[task.status?.name] || "#9ca3af",
                        }}
                      />
                    </span>

                    {/* Status text */}
                    {task.status?.name === "READY_" ? "In Progress" : task.status?.name || "-"}
                  </span>
                </div>
              </div>

              {/* Assignee & Participant */}
              <div className="detail-sections-container">
                <div className="detail-content-section1">
                  <span className="detail-label">
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.4635 15.9852V14.4628C14.4635 13.6553 14.1427 12.8808 13.5717 12.3098C13.0007 11.7388 12.2262 11.418 11.4187 11.418H6.85146C6.04392 11.418 5.26946 11.7388 4.69845 12.3098C4.12743 12.8808 3.80664 13.6553 3.80664 14.4628V15.9852" />
                      <path d="M9.13466 8.37333C10.8163 8.37333 12.1795 7.01012 12.1795 5.32851C12.1795 3.6469 10.8163 2.28369 9.13466 2.28369C7.45306 2.28369 6.08984 3.6469 6.08984 5.32851C6.08984 7.01012 7.45306 8.37333 9.13466 8.37333Z" />
                    </svg> Assigned To
                  </span>
                  <div className="assignee-info" onClick={handleAssigneeClick} style={{ cursor: "pointer" }}>
                    <img src={participantAssignee?.user?.avatar || avtar} alt="assignee avatar" />
                    <span>{participantAssignee ? `${participantAssignee.user.firstName} ${participantAssignee.user.lastName}` : "--"}</span>
                  </div>
                </div>
                <div className="detail-content-section1">
                  <span className="detail-label">
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.4635 15.9852V14.4628C14.4635 13.6553 14.1427 12.8808 13.5717 12.3098C13.0007 11.7388 12.2262 11.418 11.4187 11.418H6.85146C6.04392 11.418 5.26946 11.7388 4.69845 12.3098C4.12743 12.8808 3.80664 13.6553 3.80664 14.4628V15.9852" />
                      <path d="M9.13466 8.37333C10.8163 8.37333 12.1795 7.01012 12.1795 5.32851C12.1795 3.6469 10.8163 2.28369 9.13466 2.28369C7.45306 2.28369 6.08984 3.6469 6.08984 5.32851C6.08984 7.01012 7.45306 8.37333 9.13466 8.37333Z" />
                    </svg> Participant
                  </span>
                  <div className="assignee-info" onClick={handleParticipantClick} style={{ cursor: "pointer" }}>
                    <DefaultAvatar userId="" size="xs"/>
                    <span>{task.participant ? `${task.participant.firstName} ${task.participant.lastName}` : "--"}</span>
                  </div>
                </div>
              </div>

              {/* Due Date Section */}
              <div className="detail-content-section">
                <span className="detail-label">
                  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6.08984 1.5224V4.56722" />
                    <path d="M12.1797 1.5224V4.56722" />
                    <path d="M14.4625 3.04481H3.80561C2.96481 3.04481 2.2832 3.72641 2.2832 4.56722V15.2241C2.2832 16.0649 2.96481 16.7465 3.80561 16.7465H14.4625C15.3033 16.7465 15.9849 16.0649 15.9849 15.2241V4.56722C15.9849 3.72641 15.3033 3.04481 14.4625 3.04481Z" />
                    <path d="M2.2832 7.61205H15.9849" />
                  </svg> Due Date
                </span>
                <span className={`${dueDateStatus.className} flex items-center gap-1 flex-wrap`}>
                  <span className={dueDateStatus.svg}>
                    {/* Replaced fixed yellow circle with conditional SVG from logic */}
                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8.5" cy="8.5" r="8.5" fill="currentColor" className="opacity-20" />
                      <path d="M8.50105 3C11.5393 3 14.0021 5.46282 14.0021 8.50105C14.0021 11.5393 11.5393 14.0021 8.50105 14.0021C5.46282 14.0021 3 11.5393 3 8.50105C3 5.46282 5.46282 3 8.50105 3ZM8.5 4.1C7.3 4.1 6.2 4.6 5.4 5.4C4.6 6.2 4.1 7.3 4.1 8.5C4.1 9.7 4.6 10.8 5.4 11.6C6.2 12.4 7.3 12.9 8.5 12.9C9.7 12.9 10.8 12.4 11.6 11.6C12.4 10.8 12.9 9.7 12.9 8.5C12.9 7.3 12.4 6.2 11.6 5.4C10.8 4.6 9.7 4.1 8.5 4.1ZM8.5 5.2C8.6 5.2 8.8 5.2 8.9 5.3C9 5.4 9 5.6 9 5.7L9 5.8V8.3L10.5 9.8C10.6 9.9 10.7 10 10.7 10.1C10.7 10.3 10.7 10.4 10.6 10.5C10.5 10.6 10.3 10.7 10.2 10.7C10.1 10.7 9.9 10.7 9.8 10.6L9.8 10.5L8.1 8.9C8 8.8 8 8.7 8 8.6L8 8.5V5.8C8 5.6 8 5.5 8.1 5.4C8.2 5.3 8.4 5.2 8.5 5.2Z" fill="currentColor" />
                    </svg>
                  </span>

                  <span className={`${dueDateStatus.className}`}>
                    {moment(task.endDate).format("MMM DD, YYYY")}
                    {dueDateStatus.label}
                  </span>

                  {showExtensionButton && (
                    <span className="d-inline-block">
                      <span
                        className={`${!!closureRequest ? "request-disabled-btn" : "request-deletion-btn"} `}
                        style={{ color: !!closureRequest ? "gray " : "#7e57c2", cursor: !!closureRequest ? "not-allowed" : "pointer" }}
                        onClick={!!closureRequest ? undefined : handleShowExtendTaskRequestModal}
                      >
                        Request Extension
                      </span>
                    </span>
                  )}
                </span>
              </div>

              {/* Time Info */}
              <div className="detail-time-container">
                <div className="detail-content-section2">
                  <span className="detail-label">
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.13281 4.56723V9.13446L12.1776 10.6569" />
                      <path d="M9.13548 16.7465C13.3395 16.7465 16.7475 13.3385 16.7475 9.13446C16.7475 4.93044 13.3395 1.52242 9.13548 1.52242C4.93147 1.52242 1.52344 4.93044 1.52344 9.13446C1.52344 13.3385 4.93147 16.7465 9.13548 16.7465Z" />
                    </svg> Created
                  </span>
                  <span className="ml-[1.5rem]" style={{ color: "#101828", fontFamily: "Poppins", fontSize: "0.99906rem", fontWeight: "400" }}>
                    {moment(task.startDate).format("MMM DD, YYYY")}
                  </span>
                </div>
                <div className="detail-content-section2">
                  <span className="detail-label">
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.13281 4.56723V9.13446L12.1776 10.6569" />
                      <path d="M9.13548 16.7465C13.3395 16.7465 16.7475 13.3385 16.7475 9.13446C16.7475 4.93044 13.3395 1.52242 9.13548 1.52242C4.93147 1.52242 1.52344 4.93044 1.52344 9.13446C1.52344 13.3385 4.93147 16.7465 9.13548 16.7465Z" />
                    </svg> Last Activity
                  </span>
                  <span className="ml-[1.5rem]" style={{ color: "#101828", fontFamily: "Poppins", fontSize: "0.99906rem", fontWeight: "400" }}>
                    {activities && activities.length > 0 && activities[0].completionTime ? getTimeAgo(activities[0].completionTime) : "No activity"}
                  </span>
                </div>
              </div>
            </>
          ) : selectedSidebarTab === "instructions" ? (
            <div className="task-instructions-content">
              {task.information ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(task.information) }} />
              ) : (
                <NoDataFound title="No Task Instructions Found" description="No instructions have been defined for this task type" />
              )}
            </div>
          ) : selectedSidebarTab === "latejustify" ? (
            <div className="late-justification-content">
              {task.justificationReason ? (
                <div className="late-justification-sections">
                  {/* Due Date Section */}
                  <div className="late-justification-section">
                    <div className="late-justification-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 19 19" fill="none">
                        <path d="M6.08984 1.52246V4.56728" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.1797 1.52246V4.56728" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14.4625 3.04492H3.80561C2.96481 3.04492 2.2832 3.72653 2.2832 4.56733V15.2242C2.2832 16.065 2.96481 16.7466 3.80561 16.7466H14.4625C15.3033 16.7466 15.9849 16.065 15.9849 15.2242V4.56733C15.9849 3.72653 15.3033 3.04492 14.4625 3.04492Z" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.2832 7.61206H15.9849" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>Due Date</span>
                    </div>
                    <div className="late-justification-value">
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <circle cx="8.5" cy="8.5" r="8.5" fill="#4A5565" fillOpacity="0.15" />
                        <path d="M8.50105 3C11.5393 3 14.0021 5.46282 14.0021 8.50105C14.0021 11.5393 11.5393 14.0021 8.50105 14.0021C5.46282 14.0021 3 11.5393 3 8.50105C3 5.46282 5.46282 3 8.50105 3ZM8.50105 4.10021C7.33388 4.10021 6.21451 4.56387 5.38919 5.38919C4.56387 6.21451 4.10021 7.33388 4.10021 8.50105C4.10021 9.66823 4.56387 10.7876 5.38919 11.6129C6.21451 12.4382 7.33388 12.9019 8.50105 12.9019C9.66823 12.9019 10.7876 12.4382 11.6129 11.6129C12.4382 10.7876 12.9019 9.66823 12.9019 8.50105C12.9019 7.33388 12.4382 6.21451 11.6129 5.38919C10.7876 4.56387 9.66823 4.10021 8.50105 4.10021ZM8.50105 5.20042C8.63579 5.20044 8.76584 5.24991 8.86653 5.33944C8.96722 5.42898 9.03154 5.55235 9.04731 5.68616L9.05116 5.75053V8.27331L10.5403 9.76245C10.639 9.86144 10.6962 9.99428 10.7005 10.134C10.7048 10.2737 10.6557 10.4098 10.5633 10.5146C10.4708 10.6194 10.342 10.6851 10.2028 10.6984C10.0637 10.7116 9.92472 10.6714 9.81416 10.586L9.76245 10.5403L8.11213 8.88998C8.02663 8.80441 7.97172 8.69304 7.9559 8.57312L7.95095 8.50105V5.75053C7.95095 5.60463 8.00891 5.46471 8.11207 5.36154C8.21524 5.25838 8.35516 5.20042 8.50105 5.20042Z" fill="black" />
                      </svg>
                      <span>{moment(task.endDate).format("MMM DD, YYYY")}</span>
                    </div>
                  </div>

                  {/* Submission Date Section */}
                  <div className="late-justification-section">
                    <div className="late-justification-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 19 19" fill="none">
                        <path d="M6.08984 1.52246V4.56728" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.1797 1.52246V4.56728" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14.4625 3.04492H3.80561C2.96481 3.04492 2.2832 3.72653 2.2832 4.56733V15.2242C2.2832 16.065 2.96481 16.7466 3.80561 16.7466H14.4625C15.3033 16.7466 15.9849 16.065 15.9849 15.2242V4.56733C15.9849 3.72653 15.3033 3.04492 14.4625 3.04492Z" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.2832 7.61206H15.9849" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>Submission Date</span>
                    </div>
                    <div className="late-justification-value">
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <circle cx="8.5" cy="8.5" r="8.5" fill="#FFE2E2" />
                        <path d="M8.50105 3C11.5393 3 14.0021 5.46282 14.0021 8.50105C14.0021 11.5393 11.5393 14.0021 8.50105 14.0021C5.46282 14.0021 3 11.5393 3 8.50105C3 5.46282 5.46282 3 8.50105 3ZM8.50105 4.10021C7.33388 4.10021 6.21451 4.56387 5.38919 5.38919C4.56387 6.21451 4.10021 7.33388 4.10021 8.50105C4.10021 9.66823 4.56387 10.7876 5.38919 11.6129C6.21451 12.4382 7.33388 12.9019 8.50105 12.9019C9.66823 12.9019 10.7876 12.4382 11.6129 11.6129C12.4382 10.7876 12.9019 9.66823 12.9019 8.50105C12.9019 7.33388 12.4382 6.21451 11.6129 5.38919C10.7876 4.56387 9.66823 4.10021 8.50105 4.10021ZM8.50105 5.20042C8.63579 5.20044 8.76584 5.24991 8.86653 5.33944C8.96722 5.42898 9.03154 5.55235 9.04731 5.68616L9.05116 5.75053V8.27331L10.5403 9.76245C10.639 9.86144 10.6962 9.99428 10.7005 10.134C10.7048 10.2737 10.6557 10.4098 10.5633 10.5146C10.4708 10.6194 10.342 10.6851 10.2028 10.6984C10.0637 10.7116 9.92472 10.6714 9.81416 10.586L9.76245 10.5403L8.11213 8.88998C8.02663 8.80441 7.97172 8.69304 7.9559 8.57312L7.95095 8.50105V5.75053C7.95095 5.60463 8.00891 5.46471 8.11207 5.36154C8.21524 5.25838 8.35516 5.20042 8.50105 5.20042Z" fill="black" />
                      </svg>
                      <span>
                        {task.completionTime ? <> moment(task.completionTime).format("MMM DD, YYYY") <span className="late-submission-text"> (late submission)</span></> : "--"}

                      </span>
                    </div>
                  </div>

                  {/* Reason Section */}
                  <div className="late-justification-section">
                    <div className="late-justification-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                        <path d="M14.4635 15.9852V14.4628C14.4635 13.6553 14.1427 12.8808 13.5717 12.3098C13.0007 11.7388 12.2262 11.418 11.4187 11.418H6.85146C6.04392 11.418 5.26946 11.7388 4.69845 12.3098C4.12743 12.8808 3.80664 13.6553 3.80664 14.4628V15.9852" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.13466 8.37333C10.8163 8.37333 12.1795 7.01012 12.1795 5.32851C12.1795 3.6469 10.8163 2.28369 9.13466 2.28369C7.45306 2.28369 6.08984 3.6469 6.08984 5.32851C6.08984 7.01012 7.45306 8.37333 9.13466 8.37333Z" stroke="#4A5565" strokeWidth="1.52241" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>Reason of late submission</span>
                    </div>
                    <div className="late-justification-value late-justification-reason mt-1">
                      <span>{task.justificationReason || "--"}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <NoDataFound title="No Late Justification Found" description="No late justification has been provided for this task" />
              )}
            </div>
          ) : (
            renderExtensionHistory()
          )}
        </div>
      </div>

      {/* Weekly Supervision Notes Section */}
      <div className="weekly-supervision-notes-card">
        <div className="supervision-notes-header" onClick={() => setIsSupervisionNotesExpanded(!isSupervisionNotesExpanded)}>
          <div className="supervision-notes-title">
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 12.9444V14.7222C16.5 15.1937 16.3127 15.6459 15.9793 15.9793C15.6459 16.3127 15.1937 16.5 14.7222 16.5H2.27778C1.80628 16.5 1.3541 16.3127 1.0207 15.9793C0.687301 15.6459 0.5 15.1937 0.5 14.7222V12.9444M16.5 4.05556V2.27778C16.5 1.80628 16.3127 1.3541 15.9793 1.0207C15.6459 0.687301 15.1937 0.5 14.7222 0.5H2.27778C1.80628 0.5 1.3541 0.687301 1.0207 1.0207C0.687301 1.3541 0.5 1.80628 0.5 2.27778V4.05556" stroke="#747474" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.50022 9.38889C8.99114 9.38889 9.38911 8.99092 9.38911 8.5C9.38911 8.00908 8.99114 7.61111 8.50022 7.61111C8.0093 7.61111 7.61133 8.00908 7.61133 8.5C7.61133 8.99092 8.0093 9.38889 8.50022 9.38889Z" stroke="#747474" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14.672 8.79334C14.7384 8.60342 14.7384 8.39659 14.672 8.20667C14.1716 6.98038 13.3172 5.93095 12.2179 5.19224C11.1185 4.45352 9.82406 4.05901 8.49958 4.05901C7.17511 4.05901 5.88065 4.45352 4.78131 5.19224C3.68198 5.93095 2.82759 6.98038 2.32714 8.20667C2.26075 8.39659 2.26075 8.60342 2.32714 8.79334C2.82759 10.0196 3.68198 11.0691 4.78131 11.8078C5.88065 12.5465 7.17511 12.941 8.49958 12.941C9.82406 12.941 11.1185 12.5465 12.2179 11.8078C13.3172 11.0691 14.1716 10.0196 14.672 8.79334Z" stroke="#747474" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="ml-2">Weekly supervision notes</span>
          </div>
          <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${isSupervisionNotesExpanded ? "expanded" : ""}`} style={{ color: "#4A5565" }} />
        </div>
        {isSupervisionNotesExpanded && (
          <div className="supervision-notes-content">
            {/* <div className="supervision-notes-input-container">
              <textarea
                className="supervision-notes-textarea"
                rows={4}
                value={progressNotes}
                onChange={(e) => setProgressNotes(e.target.value)}
                placeholder="Add notes...."
                disabled={isNoteLoading}
              />
              <Button
                className={`flex rounded-xs ms-auto ${
                  isNoteLoading || (progressNoteData ? progressNotes === progressNoteData.noteText : progressNotes === "")
                    ? "bg-white text-muted border border-gray"
                    : "bg-[#7e57c2] text-white border-0 hover:bg-[#6a4aa8]"
                }`}
                onClick={handleSaveNotes}
                disabled={isNoteLoading || (progressNoteData ? progressNotes === progressNoteData.noteText : progressNotes === "")}
              >
                {isNoteLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
            <div>

            </div>

            <div>
              {progressNoteData && (
                <div className="supervision-notes-history">
                  <div className="supervision-note-timestamp">
                    {moment(progressNoteData.lastUpdatedAt).format("DD MMM, h:mm A")}
                  </div>
                  <div className="supervision-note-text whitespace-pre-wrap break-words overflow-y-auto max-h-[7.5rem] pr-2">
                    {progressNoteData.noteText}
                  </div>
                </div>
              )} */}
            {/* </div> */}
                                <div
                        className="d-flex flex-column align-items-center justify-content-center p-5 bg-light rounded"
                        style={{ border: "2px dashed #dee2e6", opacity: 0.8 }}
                    >
                        <span className="badge bg-primary mb-3 px-3 py-2">COMING SOON</span>
                        <h5 className="text-secondary fw-bold mb-1">Weekly Supervision Notes</h5>
                        <p className="text-muted mb-0 text-center" style={{ maxWidth: "300px" }}>
                            We are working on bringing Weekly Supervision management to this page soon.
                        </p>
                    </div>     
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskSidebar;